import { getPlayerFullName, getPtrLevel, lessonTypeHasPtrLevel } from "./func.utils"
import { isCancelledLesson } from "./cancellation.utils"
import {
	absencesToRecover,
	accumulateAbsences,
	getSeasonBounds,
	isSubscriptionCourse,
	regularPartecipants,
} from "./substitutes.utils"

// ──────────────────────────────────────────────────────────────────────────
// CHI POTREBBE VENIRE AL POSTO DI UN ASSENTE
//
// Un elenco da cui scegliere, non un avviso: l'app non chiama nessuno. In
// cima chi ha lezioni da recuperare, perché per lui venire non costa niente
// e per il circolo è un debito che si chiude; sotto chi gioca a un livello
// vicino e pagherebbe la volta. Gli allievi sono di tutto il circolo: un
// allievo di un collega può recuperare nel tuo CAD.
// ──────────────────────────────────────────────────────────────────────────

// Quanto può stare lontano dal gruppo, in punti PTR, chi pagherebbe la volta:
// mezzo punto sopra o sotto. Chi ha un recupero si propone a ogni livello,
// solo più in basso.
export const NEAR_PTR_LEVEL = 0.5

// Le lezioni dei sette giorni prima e dopo bastano a trovare il gruppo CAD
// di ognuno, che si ripete ogni settimana, e chi è già in campo a quell'ora.
export const NEARBY_DAYS = 7

export const MISSING_GROUP_LEVEL = "group"
export const MISSING_PLAYERS_LEVEL = "players"

const toTime = value =>
	value?.toMillis?.() ?? (value instanceof Date ? value.getTime() : null)

const ptrValue = value => (getPtrLevel(value) ? value : null)

// Il livello del gruppo: quello della lezione, e se non ce l'ha quello della
// scheda tecnica dell'assente.
export const groupPtrLevel = (lesson, absentPlayer) =>
	ptrValue(lesson?.ptrLevel) ?? ptrValue(absentPlayer?.technicalSheet?.ptrLevel)

// Il livello del gruppo CAD in cui gioca ognuno, `{ [playerID]: livello }`:
// quello della sua lezione CAD con un livello più vicina a `date`. Il gruppo
// è suo anche il giorno che manca; non di chi è venuto una volta a
// sostituire.
export const playingPtrLevels = (lessons, date) => {
	const target = toTime(date)
	const closest = {}

	;(lessons || []).forEach(lesson => {
		const level = ptrValue(lesson?.ptrLevel)
		if (!level || !lessonTypeHasPtrLevel(lesson.lessonType) || isCancelledLesson(lesson)) return
		const distance = Math.abs(toTime(lesson.lessonStart) - target)
		regularPartecipants(lesson.partecipants).forEach(player => {
			if (!player?.playerID) return
			const known = closest[player.playerID]
			if (!known || distance < known.distance)
				closest[player.playerID] = { level, distance }
		})
	})

	return Object.fromEntries(Object.entries(closest).map(([id, { level }]) => [id, level]))
}

// Chi a quell'ora è già in campo in un'altra lezione. Chi lì è segnato
// assente invece è libero, e una lezione annullata non tiene nessuno.
export const busyPlayerIDs = (lessons, lesson) => {
	const start = toTime(lesson?.lessonStart)
	const end = toTime(lesson?.lessonEnd) ?? start
	const busy = new Set()

	;(lessons || []).forEach(other => {
		if (other.lessonID === lesson?.lessonID || isCancelledLesson(other)) return
		const otherStart = toTime(other.lessonStart)
		const otherEnd = toTime(other.lessonEnd) ?? otherStart
		if (!(otherStart < end && start < otherEnd)) return
		;(other.partecipants || []).forEach(player => {
			if (player?.playerID && player.isPresent !== false) busy.add(player.playerID)
		})
	})

	return busy
}

// Le lezioni da recuperare di ognuno nella stagione della lezione e del suo
// corso, `{ [playerID]: numero }`, con le stesse regole del profilo: un CAD
// si recupera in un CAD, un SAT in un SAT, e fuori dai corsi non si recupera.
// Questa lezione non conta, come in "Come paga": un recupero che c'è già è
// quello che si sta guardando.
export const recoveriesByPlayer = (recoveryLessons, lesson) => {
	if (!isSubscriptionCourse(lesson?.lessonType)) return {}
	const { start, end } = getSeasonBounds(new Date(toTime(lesson?.lessonStart)))
	const acc = {}
	;(recoveryLessons || [])
		.filter(other => {
			const time = toTime(other.lessonStart)
			return (
				other.lessonID !== lesson?.lessonID &&
				other.lessonType === lesson.lessonType &&
				time >= start.getTime() &&
				time < end.getTime()
			)
		})
		.forEach(other => accumulateAbsences(acc, other))

	return Object.fromEntries(
		Object.values(acc)
			.filter(entry => entry.playerID)
			.map(entry => [entry.playerID, absencesToRecover(entry)]),
	)
}

// I corsi in cui gioca ognuno, `{ [playerID]: Set }`: quelli in cui sta tra
// gli allievi della lezione, anche il giorno che manca; non quelli dove è
// venuto una volta a sostituire.
export const playerCourses = lessons => {
	const courses = {}
	;(lessons || []).forEach(lesson => {
		if (!isSubscriptionCourse(lesson?.lessonType)) return
		regularPartecipants(lesson.partecipants).forEach(player => {
			if (!player?.playerID) return
			;(courses[player.playerID] ??= new Set()).add(lesson.lessonType)
		})
	})
	return courses
}

// Le proposte, in due elenchi: `recovering` chi ha recuperi da fare, `others`
// chi gioca a un livello vicino. Ognuna è `{ player, level, recoveries }`.
//
// Si ordina per distanza dal livello del gruppo, e chi un livello non ce l'ha
// va in fondo. Senza il livello del gruppo restano solo i recuperi, dal più
// lungo: "vicino" non si può dire.
//
// In un corso non si propone chi gioca solo nell'altro: un posto in un SAT
// non si riempie con chi viene al CAD, né il contrario. Chi fa solo private,
// o lezioni non ne ha, va bene per tutti e due.
//
// `missingLevel` dice perché il secondo elenco non si può fare, così il
// foglio chiede il livello che manca: `"group"` se non si sa quello del
// gruppo, `"players"` se nessuno degli allievi liberi ne ha uno; `null` se
// i livelli ci sono, e un elenco vuoto vuol dire che vicino non c'è nessuno.
export const substituteSuggestions = ({
	players,
	lesson,
	absent,
	excludedIDs,
	recoveryLessons,
	nearbyLessons,
}) => {
	const absentPlayer = (players || []).find(player => player.playerID === absent?.playerID)
	const group = groupPtrLevel(lesson, absentPlayer)
	const playing = playingPtrLevels(nearbyLessons, lesson?.lessonStart)
	const busy = busyPlayerIDs(nearbyLessons, lesson)
	const recoveries = recoveriesByPlayer(recoveryLessons, lesson)
	const courses = playerCourses([...(nearbyLessons || []), ...(recoveryLessons || [])])
	const isCourse = isSubscriptionCourse(lesson?.lessonType)
	const playsElsewhere = playerID =>
		isCourse && courses[playerID] && !courses[playerID].has(lesson.lessonType)

	const suggestions = (players || [])
		.filter(
			player =>
				player.playerID &&
				player.playerID !== absent?.playerID &&
				!excludedIDs?.has(player.playerID) &&
				!busy.has(player.playerID) &&
				!playsElsewhere(player.playerID),
		)
		.map(player => ({
			player,
			level: ptrValue(player.technicalSheet?.ptrLevel) ?? playing[player.playerID] ?? null,
			recoveries: recoveries[player.playerID] ?? 0,
		}))

	const distance = ({ level }) =>
		group === null || level === null ? Infinity : Math.abs(level - group)
	const byName = (a, b) =>
		getPlayerFullName(a.player).localeCompare(getPlayerFullName(b.player), "it")
	const byLevel = (a, b) => distance(a) - distance(b) || byName(a, b)

	const missingLevel =
		group === null
			? MISSING_GROUP_LEVEL
			: suggestions.every(suggestion => suggestion.level === null)
				? MISSING_PLAYERS_LEVEL
				: null

	return {
		missingLevel,
		recovering: suggestions
			.filter(suggestion => suggestion.recoveries > 0)
			.sort((a, b) =>
				group === null ? b.recoveries - a.recoveries || byName(a, b) : byLevel(a, b),
			),
		others:
			group === null
				? []
				: suggestions
						.filter(
							suggestion =>
								suggestion.recoveries === 0 && distance(suggestion) <= NEAR_PTR_LEVEL,
						)
						.sort(byLevel),
	}
}
